import { useQuery } from '@tanstack/react-query';
import Axios, { type AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

// Define the valid resource types
export const ResourceTypeSchema = z.enum(['invoice', 'task', 'debtor']);
export type ResourceType = z.infer<typeof ResourceTypeSchema>;

// Define the schema for comment author
const CommentAuthorSchema = z.object({
  id: z.number(),
  fullName: z.string(),
});

// Define the schema for a single comment
const CommentSchema = z.object({
  id: z.number().or(z.string().transform(Number)),
  author: CommentAuthorSchema.nullable(),
  comment: z.string(),
  date: z.string(),
});

// Define the schema for the comments response
export const CommentsResponseSchema = z.array(CommentSchema);

export type CommentResponse = z.infer<typeof CommentsResponseSchema>;

export type Comment = z.infer<typeof CommentSchema>;

export interface LoadCommentsVariables {
  resourceType: ResourceType;
  resourceId: number | string;
}

const emptyComments: CommentResponse = [];

export async function loadCommentsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadCommentsVariables,
) {
  const instance = await axiosInstance;
  const { resourceType, resourceId } = variables;

  try {
    const { data } = await instance.get('/comments', {
      params: {
        resource_type: resourceType,
        resource_id: resourceId,
      },
    });

    return CommentsResponseSchema.parse(data);
  } catch (error) {
    if (Axios.isAxiosError(error) && error.response?.status === 404) return emptyComments;
    throw error;
  }
}

export function useLoadComments(variables: LoadCommentsVariables, enabled: boolean = true) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<CommentResponse, ApiError>({
    queryKey: ['comments', variables.resourceType, variables.resourceId],
    queryFn: () => loadCommentsQueryFn(axiosInstance, variables),
    enabled,
    placeholderData: emptyComments,
  });

  return addResourceNameToQueryResult<CommentResponse, unknown, 'comments', 'PreventUndefined'>(
    'comments',
    queryResult,
    emptyComments,
  );
}
